import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { auth, googleProvider, db } from '../services/firebase';
import { User, AuthContextType, LoginCredentials, RegisterCredentials } from '../types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch user data from Firestore
  const fetchUserData = async (firebaseUser: FirebaseUser): Promise<User | null> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      if (userDoc.exists()) {
        return userDoc.data() as User;
      }
      return null;
    } catch (error) {
      console.error('Error fetching user data:', error);
      return null;
    }
  };

  // Create user document in Firestore
  const createUserDocument = async (firebaseUser: FirebaseUser, additionalData?: any) => {
    const userRef = doc(db, 'users', firebaseUser.uid);
    const now = Timestamp.now();
    const userData: any = {
      uid: firebaseUser.uid,
      email: firebaseUser.email || '',
      name: additionalData?.name || firebaseUser.displayName || '',
      phone: additionalData?.phone || '',
      photoURL: firebaseUser.photoURL || '',
      addresses: [],
      createdAt: now,
      updatedAt: now,
    };

    await setDoc(userRef, userData);
    
    // Convert Timestamps back to strings for the frontend User type
    return {
      ...userData,
      createdAt: now.toDate().toISOString(),
      updatedAt: now.toDate().toISOString(),
    } as User;
  };

  // Register with email and password
  const register = async (credentials: RegisterCredentials) => {
    try {
      const { user: firebaseUser } = await createUserWithEmailAndPassword(
        auth,
        credentials.email,
        credentials.password
      );
      
      const userData = await createUserDocument(firebaseUser, {
        name: credentials.name,
        phone: credentials.phone,
      });
      
      setUser(userData);
    } catch (error: any) {
      console.error('Registration error:', error);
      throw new Error(error.message || 'Failed to register');
    }
  };

  // Login with email and password
  const login = async (credentials: LoginCredentials) => {
    try {
      const { user: firebaseUser } = await signInWithEmailAndPassword(
        auth,
        credentials.email,
        credentials.password
      );
      
      const userData = await fetchUserData(firebaseUser);
      if (userData) {
        setUser(userData);
      }
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.message || 'Failed to login');
    }
  };

  // Login with Google
  const loginWithGoogle = async () => {
    try {
      const { user: firebaseUser } = await signInWithPopup(auth, googleProvider);
      
      let userData = await fetchUserData(firebaseUser);
      if (!userData) {
        userData = await createUserDocument(firebaseUser);
      }
      
      setUser(userData);
    } catch (error: any) {
      console.error('Google login error:', error);
      throw new Error(error.message || 'Failed to login with Google');
    }
  };

  // Logout
  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (error: any) {
      console.error('Logout error:', error);
      throw new Error(error.message || 'Failed to logout');
    }
  };

  // Update user profile
  const updateProfile = async (data: Partial<User>) => {
    if (!user) return;

    try {
      const userRef = doc(db, 'users', user.uid);
      const now = Timestamp.now();
      await updateDoc(userRef, {
        ...data,
        updatedAt: now,
      });

      setUser({ ...user, ...data, updatedAt: now.toDate().toISOString() });
    } catch (error: any) {
      console.error('Update profile error:', error);
      throw new Error(error.message || 'Failed to update profile');
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        let userData = await fetchUserData(firebaseUser);
        // If user document doesn't exist, create it
        if (!userData) {
          userData = await createUserDocument(firebaseUser);
        }
        setUser(userData);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    loginWithGoogle,
    logout,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
